import React, { useState, useEffect, useRef } from "react";
import "../../style.css";
import { 
  useAppUsageLogger // Import the new custom hook
} from "../../slices/authApi";
import { apiSlice } from "../../slices/api";
import { useNavigate } from "react-router-dom";
import ToolImage from "../../images/tool.png";
import CompImage from "../../images/compre.png";
import Spinner from "../UI/spinner/spinner";
import {
  useRecordTextMutation,
  useGenerateAssessmentBatchMutation,
  useGeneratePromptMutation,
  useGenerateTextMutation,
} from "../../slices/authApi";
import CreateReadingComprehension from "./createReadingComprehensionForm";
import CreateAssessment from "./createAssessmentForm";
import SippsQuestions from "./assesmentForm/sippsQuestions";
import classes from "./criteriaSubmission.module.css";
import util from "../../styles/utilities.module.css";
import ProgressBarWithCircles from "../Layout/ProgressBar";
import { useSelector, useDispatch } from "react-redux";
import {
  selectFormType,
  selectFormData,
  selectWritingPrompt,
  selectReadingPassage,
  setReadingPassage,
  setWritingPrompt,
  setAnchorID,
  setTextID,
  setQuizID,
  setMcQuestions,
  setFfQuestions,
  setTfQuestions,
  setFbQuestions,
  setTextImages,
  selectSelectedWorksheet,
  selectSippsLevel,
  selectSippsLesson,
  selectIncludeImages,
  selectIncludeParagraphNumbers
} from "../../slices/form";
import LanguageSelector from "./languageSelector";
import { selectLanguage } from "../../slices/auth";
import InstructionalVideo from "../ToolSelection/instructionalVideo";
import ToolDescription from "../../common/toolDescription";
import Examples from "./examples";
import boldTextDetector from "../../utils/boldTextDetector";
import { useLocation } from "react-router-dom";
import { animateScroll as scroll } from "react-scroll";

// Main CriteriaSubmission component
function CriteriaSubmission() {
  const endRef = useRef(null);
  const [exampleSelected, setExampleSelected] = useState(false);
  const dispatch = useDispatch();
  const logAppUse = useAppUsageLogger(); // Use the custom hook

  const language = useSelector(selectLanguage);
  const formType = useSelector(selectFormType);
  const formData = useSelector(selectFormData);
  const readingPassage = useSelector(selectReadingPassage);
  const selectedWorksheet = useSelector(selectSelectedWorksheet);
  const sippsLevel = useSelector(selectSippsLevel);
  const sippsLesson = useSelector(selectSippsLesson);
  const includeImages = useSelector(selectIncludeImages);
  const includeParagraphNumbers = useSelector(selectIncludeParagraphNumbers);

  const location = useLocation();
  const { state } = location;
  const [recordText, { isLoading: isRecordTextLoading }] =
    useRecordTextMutation();
  const [
    generateAssessmentBatch,
    { isLoading: isGenerateAssessmentBatchLoading },
  ] = useGenerateAssessmentBatchMutation();
  const [generatePrompt, { isLoading: isGeneratePromptLoading }] =
    useGeneratePromptMutation();
  const [generateText, { isLoading: isGenerateTextLoading }] =
    useGenerateTextMutation();

  const [errorMsg, setErrMsg] = useState("");
  const [difficultyLevelError, setDifficultyLevelError] = useState("");

  const [userInfo, setUserInfo] = useState(null);
  const userGroup = userInfo?.user_options?.user_group || "";

  const navigate = useNavigate();

  const exampleSetHandler = (set, title) => {
    logAppUse( // Use the new logger function
      `criteria_selection_${formType}`,
      `template:${formType}:${title}`
    );
    setExampleSelected(set);
    setDifficultyLevelError("");
    setErrMsg("");
  };

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user"))?.userData;
    setUserInfo(user);
    setErrMsg("");
  }, []);

  useEffect(() => {
    const from = localStorage.getItem("from");

    if (formType === "assessment" && from === "toolSelection")
      logAppUse("tool_selection", "criteria_selection_assessment"); // Use the new logger function
    else if (formType === "reading" && from === "toolSelection")
      logAppUse( // Use the new logger function
        "tool_selection",
        "criteria_selection_reading_comprehension"
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formType]);

  // app usage endpoint

  // Handle form submission and send data to the server
  async function handleSubmit(event) {
    event.preventDefault();
    const buttonDir = event.nativeEvent.submitter.name;
    
    if (buttonDir === "right") {
      const normalizedSubject =
        typeof formData?.subject === "string" ? formData.subject.trim() : "";
      const requiresGenre =
        formType !== "sipps" &&
        normalizedSubject.length > 0 &&
        normalizedSubject.toLowerCase() !== "math";
      const genreValue = formData?.genre;
      const normalizedGenre =
        typeof genreValue === "string" ? genreValue.trim() : genreValue;
      const genreMissing =
        requiresGenre &&
        (!normalizedGenre ||
          (typeof normalizedGenre === "string" &&
            (normalizedGenre.length === 0 ||
              normalizedGenre.toLowerCase() === "none selected")));

      if (!formData?.difficulty_level && formType!=="sipps") {
        setDifficultyLevelError("Please select a difficulty level to continue");
        setErrMsg("");
      } else if (formData?.grade?.length < 1 && formType!=="sipps") {
        setErrMsg("Select grade");
        setDifficultyLevelError("");
      } else if (
        formData?.questions?.mc_quantity > 20 ||
        formData?.questions?.mc_quantity < 0
      ) {
        setErrMsg("Please enter an integer between 0 and 20.");
        setDifficultyLevelError("");
      } else if (
        formData?.questions?.ff_quantity > 20 ||
        formData?.questions?.ff_quantity < 0
      ) {
        setErrMsg("Please enter an integer between 0 and 20.");
        setDifficultyLevelError("");
      } else if (
        formData?.questions?.tf_quantity > 20 ||
        formData?.questions?.tf_quantity < 0
      ) {
        setErrMsg("Please enter an integer between 0 and 20.");
        setDifficultyLevelError("");
      }else if (
        formData?.questions?.fb_quantity > 20 ||
        formData?.questions?.fb_quantity < 0
      ) {
        setErrMsg("Please enter an integer between 0 and 20.");
        setDifficultyLevelError("");
      }else if (
        formData?.questions?.fb_quantity + formData?.questions?.tf_quantity + formData?.questions?.ff_quantity + formData?.questions?.mc_quantity>20
      ) {
        setErrMsg("We only allow 20 questions at first.  You can always generate more after you submit.");
        setDifficultyLevelError("");
      }
      else if (!formData?.show_text && formType!=="sipps") {
        setErrMsg("Select test type");
        setDifficultyLevelError("");
      } else if (genreMissing) {
        setErrMsg("Select genre");
        setDifficultyLevelError("");
      } else if (formType === "reading" && formData?.length?.length < 1) {
        setErrMsg("Select length");
        setDifficultyLevelError("");
      } else if (formData?.subject?.length < 1 && formType!=="sipps") {
        setErrMsg("Select Subject");
        setDifficultyLevelError("");
       } else if (
        formType === "assessment" &&
        (!readingPassage || readingPassage.trim().length < 1) &&
        (!formData?.text_files || formData?.text_files?.length < 1)
      ) {
        setErrMsg("Upload Content");
        setDifficultyLevelError("");
      } else if (
        formType === "reading" &&
        (!formData?.text || formData.text.trim().length < 1)
      ) {
        setErrMsg("Upload Content");
        setDifficultyLevelError("");
      } else {
        setErrMsg("");
        try {
          if (formType === "assessment") {
            // Guard: ensure question_scope_passage_only is present on questions before sending
            const questionScopePresent =
              typeof formData?.questions?.question_scope_passage_only === "boolean";
            const normalizedQuestions = {
              ...(formData?.questions || {}),
              ...(questionScopePresent
                ? {}
                : { question_scope_passage_only: formData?.show_text !== "Open book" }),
            };

            const recordTextData = await recordText({
              body: {
                ...formData,
                questions: normalizedQuestions,
                language,
              },
              record_location: "text-criteria", 
            }).unwrap();

            const textID = recordTextData.text_id;

            const assessmentBatchData = await generateAssessmentBatch({
              text_id: textID,
              show_text: formData.show_text,
              template_type: formType
            }).unwrap();

            // Assigning variables from response (RTK query wraps payload inside data)
            const assessmentPayload = assessmentBatchData?.data ?? assessmentBatchData ?? {};
            const {
              quiz_id: quizID,
              mc_questions: mcQuestions = [],
              ff_questions: ffQuestions = [],
              tf_questions: tfQuestions = [],
              fb_questions: fbQuestions = [],
              upload_data: uploadData = {},
              extracted_text: extractedText = null,
            } = assessmentPayload;
            
            

            dispatch(setTextID(textID));
            dispatch(setQuizID(quizID));
            dispatch(setMcQuestions(mcQuestions));
            dispatch(setFfQuestions(ffQuestions));
            dispatch(setTfQuestions(tfQuestions));
            dispatch(setFbQuestions(fbQuestions));
            
            // Set reading passage only if user provided text in the form
            if (formData.text && formData.text.trim() !== "") {
              dispatch(setReadingPassage(formData.text));
            }
            // Don't set readingPassage for extracted text - let questionsReview handle it
            
            logAppUse( // Use the new logger function
              "criteria_selection_assessment",
              "questions_review"
            );
            
            navigate("/questionsReview", { 
              state: { 
                uploadedFileData: uploadData,
                extractedText: extractedText 
              } 
            });
            
            
          }else if(formType==="sipps"){
            // Invalidate cache to ensure fresh worksheet data, and remove image so an old one doesn't show up
            dispatch(apiSlice.util.invalidateTags(['SippsWorksheetData']));
            dispatch(setTextImages([]));
            if (selectedWorksheet>=1 && selectedWorksheet<=8){
              logAppUse("W"+selectedWorksheet+"_"+sippsLevel+"_"+sippsLesson, "create_worksheet"); // Use the new logger function
              navigate('/worksheet');
            }
          } 
          else {
            // Reading comprehension
            const generatePromptData = await generatePrompt({
              ...formData,
              language
            }).unwrap();

            const writingPrompt = {
              content: generatePromptData.writing_prompt,
              edited: false,
            };
            // SAVE writingPrompt to forms.js
            dispatch(setWritingPrompt(writingPrompt));
            let anchorID = generatePromptData.anchor_id;

            const generateTextData = await generateText({
              anchor_id: anchorID,
              regenerate: false,
              writing_prompt: writingPrompt,
              sipps_lesson: sippsLesson,
              sipps_level: sippsLevel,
              include_images: includeImages,
              include_paragraph_numbers: includeParagraphNumbers,
              image_instructions: formData.image_instructions,
              image_speed: formData.image_speed
            }).unwrap();

            // Assigning variables from response

            const readingPassage =
              (generateTextData &&
                generateTextData.text &&
                generateTextData.text.trim()) ||
              "the returned text key is undefined";
            const textID = generateTextData?.text_id;
            const textImages = generateTextData?.images;

            // SAVE readingPassage to forms.js
            dispatch(setReadingPassage(readingPassage));
            dispatch(setTextID(textID));
            dispatch(setAnchorID(anchorID));
            dispatch(setTextImages(textImages));
            logAppUse( // Use the new logger function
              "criteria_selection_reading_comprehension",
              "passage_review"
            );
            // Navigate to the required path
            navigate("/PassageReview");
          }
        } catch (err) {
          if (!err?.status) {
            // isLoading: true until timeout occurs
            setErrMsg("No Server Response");
          } else if (err.status === 401) {
            setErrMsg("Unauthorized");
          } else {
            let err_message = "";
            if (err.data && err.data.message) {
              err_message = err.data.message;
            } else {
              err_message = err.status + " Server Error ";
            }
            setErrMsg(err_message);
          }
        }
      }
    }
  }

  const backBtnClickHandler = () => {
    if (formType === "assessment")
      logAppUse("criteria_selection_assessment", "tool_selection"); // Use the new logger function
    else if (formType === "reading")
      logAppUse( // Use the new logger function
        "criteria_selection_reading_comprehension",
        "tool_selection"
      );

    navigate(-1);
  };

  const writingPrompt = useSelector(selectWritingPrompt);

  const writingPromptOverlay = () => {
    return (
      <div className={classes.EagerBox}>
        <p className={classes.EagerHeading}>
          Eager to see what we are crafting?
        </p>
        <p className={classes.EagerParagraph}>
          Here is a glimpse at the writing prompt generated from your unique
          inputs.
        </p>
        <div className={classes.EagerItalicParagraphHeight}>
          <i className={classes.EagerItalicParagraph}>
            {boldTextDetector(writingPrompt?.content)}
          </i>
        </div>
      </div>
    );
  };

  useEffect(() => {
    if (state && state.showScroll) {
      // endRef?.current?.scrollIntoView({ behavior: "smooth" });
      scroll.scrollTo(900, {duration: 2000})
      setExampleSelected(true);
    }
  }, [state]);

  return (
    <div className="page-margin">
      {(isRecordTextLoading ||
        isGenerateAssessmentBatchLoading ||
        isGeneratePromptLoading ||
        isGenerateTextLoading) && (
        <Spinner
          overlayText={isGenerateTextLoading ? writingPromptOverlay() : null}
          message={
            formType === "assessment"
              ? "We are hard at work creating your quiz. This may take a few minutes."
              : "We are hard at work creating your text. This may take a few minutes."
          }
        />
      )}
      <section
        className="app-main"
        {...(formType === "sipps" ? { role: "region", "aria-label": "sipps" } : {})}
      >
        <p style={{ fontSize: "larger", textAlign: "center" }}>
          <strong style={{ fontSize: "19px" }}>Enter your criteria.</strong>
          <br />
          Personalize the quiz or practice to fit your needs.
        </p>
        <div style={{ paddingTop: "60px" }} />

        <ProgressBarWithCircles progress={25} />
        <div className={classes.videoAndTool}>
          <ToolDescription
            image={formType === "assessment" ? ToolImage : CompImage}
            heading={
              formType === "assessment"
                ? `Create questions for a text or list of terms you upload.`
                : formType === "reading" 
                    ? `Create an original text along with a set of questions.`
                    :  `Create various reading worksheets targeted at a specific level of your school's ELA curriculum.`
            }
            description={
              formType === "assessment"
                ? `This tool allows you to generate a set of questions 
                          for content you upload. Your content can be a fictional 
                          or non-fiction text, a textbook chapter, a poem, 
                          a lesson plan, a syllabus, a list of terms, a list of 
                          vocab words, etc. You will be able to export the questions, 
                          along with the answer key, to a Google Doc, Google Form, Kahoot, or Blooket sheet.`
                : formType === "reading"
                    ? `This tool allows you to create a brand new, original text based on a topic or 
                        a description of what you'd like the AI to write about. We 
                        support a wide range of genres, covering both fiction and 
                        non-fiction. Once the text has been created, you can generate 
                        a set of questions for the text. You will be able to export the 
                        text and questions, along with the answer key, 
                        to a Google Doc, Google Form, Kahoot, or Blooket sheet.`
                    : `This tool allows you to generate matching, cut and paste, and reading practice
                        worksheets that target the selected level and lesson number of your school's ELA curriculum.`
            }
          />

          {formType !== 'sipps' && (
            <InstructionalVideo
              formType={formType}
              logAppUse={logAppUse} // Pass the new logger function directly
            />
          )}
        </div>
        {formType !== 'sipps' && userGroup && ( // Ensure userGroup is defined
          <Examples
            scrollToRef={endRef}
            setExampleSelected={exampleSetHandler}
            userGroup={userGroup}
          />
        )}

  {formType !== 'sipps' &&(<LanguageSelector />)}
  {/* Scroll anchor for example selection */}
  <div ref={endRef} aria-hidden="true" />
  <form onSubmit={handleSubmit} aria-label="Criteria selection form">
          {formType === "assessment" ? <CreateAssessment isLoading={isRecordTextLoading || isGenerateAssessmentBatchLoading} /> : null}
          {formType === "reading" ? <CreateReadingComprehension isLoading={isGenerateTextLoading || isGeneratePromptLoading} /> : null}
          {formType === "sipps" ? <SippsQuestions /> : null}
          {errorMsg ? (
            <div className={`${util.alert} ${util['alert--danger']}`} role="alert" aria-live="polite">{errorMsg}</div>
          ) : null}
          {difficultyLevelError ? (
            <div className={`${util.alert} ${util['alert--danger']}`} role="alert" aria-live="polite">{difficultyLevelError}</div>
          ) : null}
          {exampleSelected ? (
            <h4 className={classes["example-message"]} role="status" aria-live="polite">
              {formData?.template_type === "reading"
                ? "You can edit the form above, or click Continue to generate an original text with questions."
                : "You can edit the form above, or click Continue to generate your questions."}
            </h4>
          ) : null}
          <div className={classes.navigationButtonsContainer}>
            <div
              className={`${classes['form-actions']} ${classes['form-actions--flow']}`}
              style={{
                paddingTop: errorMsg || exampleSelected ? "0px" : "40px",
              }}
            >
              <button
                className={`${util.btn} ${util['btn--ghost']} ${classes.navigationButton}`}
                onClick={backBtnClickHandler}
                type="button"
                aria-label="Go back to previous page"
              >
                Go back
              </button>

              <button
                className={`${util.btn} ${util['btn--secondary']} ${classes.navigationButton}`}
                name={"right"}
                disabled={!formType}
                type="submit"
                aria-label={formType === "assessment" ? "Continue to generate questions" : formType === "reading" ? "Continue to generate text and questions" : "Continue to next step"}
              >
                Continue
              </button>
            </div>
          </div>
        </form>
      </section>
    </div>
  );
}

export default CriteriaSubmission;
